import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import SessionsPage from '@/pages/SessionsPage';
import PatientsPage from '@/pages/PatientsPage';
import PatientDetailPage from '@/pages/PatientDetailPage';
import QuestionnairesPage from '@/pages/QuestionnairesPage';
import TransactionsPage from '@/pages/TransactionsPage';
import MessagesPage from '@/pages/MessagesPage';
import TestimonialsPage from '@/pages/TestimonialsPage';
import SettingsPage from '@/pages/SettingsPage';
import BranchesPage from '@/pages/BranchesPage';
import TherapistsPage from '@/pages/TherapistsPage';
import ReportsPage from '@/pages/ReportsPage';
import EmailLogsPage from '@/pages/EmailLogsPage';
import ProgressPage from '@/pages/ProgressPage';
import QuestionnaireBuilderPage from '@/pages/QuestionnaireBuilderPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import InvoicingPage from '@/pages/InvoicingPage';
import InvoiceFormPage from '@/pages/InvoiceFormPage';
import InvoiceDetailPage from '@/pages/InvoiceDetailPage';
import { Toaster } from '@/components/ui/sonner';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4FAF9]">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-[#0D8B7D] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-sm text-[#4A6B68]">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4FAF9]">
      <div className="w-10 h-10 border-2 border-[#0D8B7D] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/sessions" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
      <Route path="/patients" element={<ProtectedRoute roles={['THERAPIST', 'ADMIN']}><PatientsPage /></ProtectedRoute>} />
      <Route path="/patients/:id" element={<ProtectedRoute roles={['THERAPIST', 'ADMIN']}><PatientDetailPage /></ProtectedRoute>} />
      <Route path="/questionnaires" element={<ProtectedRoute><QuestionnairesPage /></ProtectedRoute>} />
      <Route path="/questionnaires/builder" element={<ProtectedRoute roles={['ADMIN']}><QuestionnaireBuilderPage /></ProtectedRoute>} />
      <Route path="/questionnaires/builder/:id" element={<ProtectedRoute roles={['ADMIN']}><QuestionnaireBuilderPage /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/testimonials" element={<ProtectedRoute><TestimonialsPage /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute roles={['CLIENT']}><ProgressPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/branches" element={<ProtectedRoute roles={['ADMIN']}><BranchesPage /></ProtectedRoute>} />
      <Route path="/therapists" element={<ProtectedRoute roles={['ADMIN']}><TherapistsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute roles={['THERAPIST', 'ADMIN']}><ReportsPage /></ProtectedRoute>} />
      <Route path="/email-logs" element={<ProtectedRoute roles={['ADMIN']}><EmailLogsPage /></ProtectedRoute>} />
      <Route path="/invoicing" element={<ProtectedRoute roles={['THERAPIST', 'ADMIN']}><InvoicingPage /></ProtectedRoute>} />
      <Route path="/invoicing/invoice/new" element={<ProtectedRoute roles={['THERAPIST', 'ADMIN']}><InvoiceFormPage /></ProtectedRoute>} />
      <Route path="/invoicing/invoice/:id" element={<ProtectedRoute roles={['THERAPIST', 'ADMIN']}><InvoiceDetailPage /></ProtectedRoute>} />
      <Route path="/invoicing/invoice/:id/edit" element={<ProtectedRoute roles={['THERAPIST', 'ADMIN']}><InvoiceFormPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
